import { getApiUrl } from './config';

// API base is resolved at call time via getApiUrl(), which reads from
// the runtime config service. The config must have been loaded before
// any function in this module is called (the boot gate in App.js
// awaits loadConfig() before rendering).
//
// This indirection is what lets the same built bundle point at
// different backends across environments without rebuilding.
const SESSION_KEY = 'sigmacore_session_id';
// Set when handle401() fires so the login view knows whether the user
// landed there because their session expired (show the banner) or
// because they explicitly logged out (don't show it).
const SESSION_EXPIRED_KEY = 'sigmacore_session_expired';

/**
 * Sentinel thrown when an authenticated request returns 401.
 * App-level catch handlers recognise this and skip the error banner —
 * the unauthorized event listener handles the redirect to login.
 */
export class UnauthorizedError extends Error {
  constructor() {
    super('UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

// ---- Clerk token bridge ----------------------------------------------------
//
// Auth is owned by Clerk (F1). Clerk's session token is fetched via an
// ASYNC getToken() that lives on the React `useAuth()` hook — there is no
// JWT in localStorage anymore. To keep this non-React module able to
// attach a Bearer header, App.js registers Clerk's getToken here once it
// mounts. authHeaders() then awaits it per request so every call carries
// a fresh, unexpired token.
let _tokenGetter = null;

/**
 * Register the async token getter (Clerk's `getToken`). Call once from a
 * component that has access to `useAuth()`. Passing null clears it (e.g.
 * on sign-out) so subsequent requests go out unauthenticated.
 */
export function registerTokenGetter(fn) {
  _tokenGetter = fn;
}

// Build headers with Authorization attached when a Clerk token is
// available. Async because Clerk's getToken() is async.
async function authHeaders(extra = {}) {
  if (!_tokenGetter) return extra;
  let token = null;
  try {
    token = await _tokenGetter();
  } catch {
    // A failed token fetch (e.g. signed out mid-flight) → send the
    // request unauthenticated and let the 401 path handle redirect.
    token = null;
  }
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

/**
 * Centralised handling for 401 responses on protected endpoints.
 *   - Clears the JWT and any cached session_id from localStorage so a
 *     refresh re-renders the login screen instead of trying to use
 *     dead credentials.
 *   - Sets a one-shot "session expired" flag so AuthPage can show a
 *     friendly banner explaining what happened.
 *   - Dispatches `sigmacore:unauthorized` so the App shell can flip
 *     state (isAuthenticated → false) without waiting for the next
 *     mount.
 *   - Returns true so callers can short-circuit and throw an
 *     UnauthorizedError sentinel, which the App-level handler swallows
 *     instead of surfacing as a "Create session failed: 401" banner.
 */
function handle401(resp) {
  if (resp.status !== 401) return false;
  // Clerk owns the token; nothing to clear from localStorage except our
  // cached session id. The App-level unauthorized listener triggers a
  // Clerk signOut() so the user is routed back to the sign-in screen.
  localStorage.removeItem(SESSION_KEY);
  localStorage.setItem(SESSION_EXPIRED_KEY, '1');
  window.dispatchEvent(new CustomEvent('sigmacore:unauthorized'));
  return true;
}

// Read-once accessors for the session-expired flag — used by AuthPage.
export function consumeSessionExpiredFlag() {
  const flagged = localStorage.getItem(SESSION_EXPIRED_KEY) === '1';
  if (flagged) localStorage.removeItem(SESSION_EXPIRED_KEY);
  return flagged;
}

// ---- User profile endpoints (F1) -------------------------------------------

/**
 * Fetch the authenticated user's profile. `profile_complete` is false on
 * first login (company_name not yet collected) — App.js uses it to gate
 * the profile-completion screen before the interview.
 */
export async function getUserProfile() {
  const resp = await fetch(`${getApiUrl()}/users/me`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get profile failed: ${resp.status}`);
  return resp.json();
}

/**
 * Update the profile (company_name required by the backend; phone
 * optional). Returns the updated profile including profile_complete.
 */
export async function updateUserProfile({ company_name, phone }) {
  const resp = await fetch(`${getApiUrl()}/users/me`, {
    method: 'PATCH',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ company_name, phone }),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err?.detail) {
        detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
      }
    } catch { /* ignore non-JSON body */ }
    throw new Error(`Update profile failed: ${detail}`);
  }
  return resp.json();
}

// ---- Session endpoints (all Clerk-protected) -------------------------------

export async function createSession() {
  const resp = await fetch(`${getApiUrl()}/sessions`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({}),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Create session failed: ${resp.status}`);
  return resp.json();
}

export async function getSession(sessionId) {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get session failed: ${resp.status}`);
  return resp.json();
}

/**
 * Mark a session as abandoned on the server. The "New Plan" button
 * calls this BEFORE creating a replacement so the old session stops
 * surfacing on GET /sessions/active. Idempotent on the backend —
 * calling on an already-abandoned/completed session is a safe no-op.
 */
export async function abandonSession(sessionId) {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}/abandon`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok && resp.status !== 404) {
    // A 404 is fine — the session may have already been cleaned up.
    // Any other failure is worth surfacing so the UI can decide.
    throw new Error(`Abandon session failed: ${resp.status}`);
  }
}

/**
 * Return the user's most recent incomplete session, or null.
 * Called right after login so the UI can resume instead of starting fresh.
 */
export async function getActiveSession() {
  const resp = await fetch(`${getApiUrl()}/sessions/active`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get active session failed: ${resp.status}`);
  return resp.json();   // may be null
}

/**
 * Full session state (stage + collected inputs) for resume flows.
 * Separate from getSession so we can evolve the response shape without
 * disturbing GET /sessions/{id} callers.
 */
export async function getSessionState(sessionId) {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}/state`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get session state failed: ${resp.status}`);
  return resp.json();
}

// Server-side conversation transcript for display/restore. Returns the stored
// messages ([{role, content, created_at}], oldest first) so a resumed session
// can rebuild the chat thread across logout/login and device switches.
export async function getSessionMessages(sessionId) {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}/messages`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get session messages failed: ${resp.status}`);
  const data = await resp.json();
  return data.messages || [];
}

export function sendMessageSSE(sessionId, message, onText, onDone, onError) {
  const controller = new AbortController();

  // Build the request inside an async IIFE so we can await the Clerk
  // token while still returning the synchronous abort handle below.
  (async () => {
    const headers = await authHeaders({ 'Content-Type': 'application/json' });
    return fetch(`${getApiUrl()}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
      signal: controller.signal,
    });
  })()
    .then(async (resp) => {
      if (!resp.ok) {
        // 401 → handle401 already cleared state and dispatched the
        // unauthorized event. Don't surface the raw 401 in the chat
        // — the user is being redirected to login.
        if (handle401(resp)) return;
        const err = await resp.json().catch(() => ({ detail: resp.statusText }));
        onError(err.detail || 'Request failed');
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            // Pass the server-issued event id through to the callbacks so
            // the UI can dedupe if the same stream is processed twice.
            if (event.type === 'text') {
              onText(event.content, event.id);
            } else if (event.type === 'done') {
              onDone(event.stage, event.id);
            } else if (event.type === 'error') {
              onError(event.detail, event.id);
            }
          } catch {
            // skip malformed events
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err.message);
      }
    });

  return () => controller.abort();
}

// Thrown when no plan matches the session's CURRENT answers — either none
// was generated yet, or the answers changed since the last calculation (the
// backend no longer serves a stale pre-change plan). Callers show a
// "recalculate" prompt, not a generic error.
export class PlanNotFoundError extends Error {
  constructor() {
    super('No plan for the current answers');
    this.name = 'PlanNotFoundError';
  }
}

export async function getPlan(sessionId) {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}/plan`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (resp.status === 404) throw new PlanNotFoundError();
  if (!resp.ok) throw new Error(`Get plan failed: ${resp.status}`);
  return resp.json();
}

// ---- Blob download helper --------------------------------------------------

/**
 * Fetch a binary endpoint (authed GET) and trigger a browser download.
 *
 * Standard React blob-download pattern: fetch → blob → a temporary <a> with
 * href = URL.createObjectURL(blob), click it, then revoke the object URL.
 * The filename prefers the server's Content-Disposition, falling back to
 * `fallbackName`.
 */
export async function downloadFile(path, fallbackName) {
  const resp = await fetch(`${getApiUrl()}${path}`, {
    headers: await authHeaders(),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err?.detail) detail = err.detail;
    } catch { /* binary / empty body */ }
    throw new Error(`Download failed: ${detail}`);
  }

  const disposition = resp.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match ? match[1] : fallbackName;

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return { filename };
}

// ---- Business plan (F3) -----------------------------------------------------

/** Per-section generation status for the BusinessPlanPanel. */
export async function getBusinessPlanStatus(sessionId) {
  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/business-plan/status`,
    { headers: await authHeaders() },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get business-plan status failed: ${resp.status}`);
  return resp.json();
}

export function downloadBusinessPlanDocx(sessionId) {
  return downloadFile(
    `/sessions/${sessionId}/business-plan/docx`,
    'business_plan.docx',
  );
}

export function downloadBusinessPlanPdf(sessionId) {
  return downloadFile(
    `/sessions/${sessionId}/business-plan/pdf`,
    'business_plan.pdf',
  );
}

// ---- SBA package (F4) -------------------------------------------------------

/** Manifest of uploaded passthrough documents + supported types. */
export async function getSbaUploads(sessionId) {
  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/sba-package/uploads`,
    { headers: await authHeaders() },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get SBA uploads failed: ${resp.status}`);
  return resp.json();
}

/** Pre-submission checklist (JSON) driving the SbaPackagePanel. */
export async function getSbaChecklist(sessionId) {
  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/sba-package/checklist`,
    { headers: await authHeaders() },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Get SBA checklist failed: ${resp.status}`);
  return resp.json();
}

/**
 * Upload a passthrough document (PDF). Multipart form-data — we deliberately
 * do NOT set Content-Type so the browser supplies the multipart boundary.
 */
export async function uploadSbaDocument(sessionId, documentType, principalIndex, file) {
  const form = new FormData();
  form.append('document_type', documentType);
  if (principalIndex != null) form.append('principal_index', String(principalIndex));
  form.append('file', file);

  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/sba-package/upload`,
    { method: 'POST', headers: await authHeaders(), body: form },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err?.detail) detail = err.detail;
    } catch { /* non-JSON body */ }
    throw new Error(detail);
  }
  return resp.json();
}

export function downloadSbaPackage(sessionId) {
  return downloadFile(
    `/sessions/${sessionId}/sba-package/zip`,
    'sba_package.zip',
  );
}

// --- F5 Upload-Actuals (operating businesses) ------------------------------

export async function getActuals(sessionId) {
  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/actuals`,
    { headers: await authHeaders() },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`Failed to load actuals: ${resp.status}`);
  return resp.json();
}

export async function uploadActualsDocument(sessionId, documentType, file) {
  const form = new FormData();
  form.append('document_type', documentType);
  form.append('file', file);

  const resp = await fetch(
    `${getApiUrl()}/sessions/${sessionId}/actuals/upload`,
    { method: 'POST', headers: await authHeaders(), body: form },
  );
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err?.detail) detail = err.detail;
    } catch { /* non-JSON body */ }
    throw new Error(detail);
  }
  return resp.json();
}

export async function exportPlan(sessionId, format = 'excel') {
  const resp = await fetch(`${getApiUrl()}/sessions/${sessionId}/export`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ format }),
  });
  if (handle401(resp)) throw new UnauthorizedError();
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err?.detail) detail = err.detail;
    } catch { /* response body was not JSON — e.g. truncated file */ }
    throw new Error(`Export failed: ${detail}`);
  }

  // Prefer the filename the backend supplies; fall back to a sane default.
  const disposition = resp.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match ? match[1] : `sigmacore_plan.${format === 'excel' ? 'xlsx' : 'csv'}`;

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return { filename };
}
